import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  CreditCard,
  FolderKanban,
  LayoutDashboard,
  Mail,
  MessageSquarePlus,
  Settings,
  User,
  Zap,
  History,
  Users,
  Files,
} from 'lucide-react';
import { APP_ROUTES } from '@config/routes';

export interface DashboardNavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  section: 'Main' | 'Clients' | 'Shortcuts' | 'Workspace' | 'Settings' | 'Invoicing';
  description: string;
  keywords: string[];
}

export const mainNav: DashboardNavItem[] = [
  {
    name: 'Dashboard',
    href: APP_ROUTES.DASHBOARD,
    icon: LayoutDashboard,
    section: 'Main',
    description: 'View your dashboard overview, activity, and daily assistant performance.',
    keywords: ['home', 'overview', 'stats'],
  },
  {
    name: 'Upcoming',
    href: APP_ROUTES.CALENDAR,
    icon: Calendar,
    section: 'Main',
    description: 'View and manage your bookings, planner, and scheduled appointments.',
    keywords: ['calendar', 'bookings', 'planner', 'appointments', 'meetings', 'schedule'],
  },
];

export const clientsNav: DashboardNavItem[] = [
  {
    name: 'Projects',
    href: APP_ROUTES.PROJECTS,
    icon: FolderKanban,
    section: 'Main',
    description: 'Manage your leads and client projects from inquiry through delivery.',
    keywords: ['jobs', 'leads', 'messages', 'conversations'],
  },
  {
    name: 'Messages',
    href: APP_ROUTES.EMAIL,
    icon: Mail,
    section: 'Shortcuts',
    description: 'Manage synced emails, chat messages, drafts, and follow-ups.',
    keywords: ['email', 'emails', 'gmail', 'inbox', 'messages', 'chat', 'conversations'],
  },
  {
    name: 'Clients',
    href: APP_ROUTES.CLIENTS,
    icon: Users,
    section: 'Clients',
    description:
      'Browse client profiles and aggregate their projects, events, and communication history.',
    keywords: ['clients', 'people', 'leads', 'contacts', 'events', 'projects'],
  },
];

export const shortcutsNav: DashboardNavItem[] = [
  {
    name: 'Workflow',
    href: APP_ROUTES.WORKFLOW,
    icon: Zap,
    section: 'Shortcuts',
    description: 'View and manage workflows for each project from a single place.',
    keywords: ['workflow', 'project workflow', 'automation', 'tasks'],
  },
  {
    name: 'Templates',
    href: APP_ROUTES.TEMPLATES,
    icon: Files,
    section: 'Shortcuts',
    description:
      'Manage reusable templates for quotes, invoices, contracts, questionnaires, and emails.',
    keywords: [
      'templates',
      'quotes',
      'invoicing',
      'invoices',
      'forms',
      'contracts',
      'questionnaires',
      'emails',
    ],
  },
  {
    name: 'Settings',
    href: APP_ROUTES.SETTINGS,
    icon: Settings,
    section: 'Shortcuts',
    description: 'Configure business details, templates, and system preferences.',
    keywords: ['preferences', 'templates', 'configuration'],
  },
];

export const supportNavItem: DashboardNavItem = {
  name: 'Help & Support',
  href: APP_ROUTES.SUPPORT,
  icon: MessageSquarePlus,
  section: 'Shortcuts',
  description: 'Browse help articles, troubleshoot issues, and open support tickets.',
  keywords: ['help', 'support', 'ticket', 'feature request', 'bug report', 'faq'],
};

const workspaceNav: DashboardNavItem[] = [
  {
    name: 'Billing',
    href: APP_ROUTES.BILLING,
    icon: CreditCard,
    section: 'Workspace',
    description: 'Manage your plan, invoices, and subscription details.',
    keywords: ['subscription', 'plan', 'payments'],
  },
  {
    name: 'Widget Settings',
    href: APP_ROUTES.WIDGET_SETTINGS,
    icon: Zap,
    section: 'Workspace',
    description: 'Customize your AI widget behavior, appearance, and embed settings.',
    keywords: ['widget', 'chat', 'embed'],
  },
  {
    name: 'Account Settings',
    href: APP_ROUTES.ACCOUNT_SETTINGS,
    icon: User,
    section: 'Workspace',
    description: 'Update your profile, preferences, and account-level settings.',
    keywords: ['profile', 'account', 'preferences'],
  },
  {
    name: 'Changelog',
    href: APP_ROUTES.CHANGELOG,
    icon: History,
    section: 'Workspace',
    description: 'Review recently shipped features, improvements, and fixes.',
    keywords: ['updates', 'release notes', 'history'],
  },
];

const settingsSearchNav: DashboardNavItem[] = [
  {
    name: 'Company Details',
    href: `${APP_ROUTES.SETTINGS}?section=company-details`,
    icon: Settings,
    section: 'Settings',
    description: 'Manage business identity details including your company logo and branding.',
    keywords: ['company logo', 'logo', 'branding', 'business name', 'brand assets'],
  },
  {
    name: 'Currency & Taxes',
    href: `${APP_ROUTES.SETTINGS}?section=currency-taxes`,
    icon: Settings,
    section: 'Settings',
    description: 'Configure currency format, tax preferences, and financial defaults.',
    keywords: ['currency', 'tax', 'financial settings'],
  },
  {
    name: 'Tax Rates',
    href: `${APP_ROUTES.SETTINGS}?section=tax-rates`,
    icon: Settings,
    section: 'Settings',
    description: 'Create and edit reusable tax rate templates.',
    keywords: ['vat', 'gst', 'sales tax', 'tax templates'],
  },
  {
    name: 'Date, Time & Calendar',
    href: `${APP_ROUTES.SETTINGS}?section=date-time-calendar`,
    icon: Settings,
    section: 'Settings',
    description: 'Set timezone, date display, and connected calendar preferences.',
    keywords: ['timezone', 'calendar', 'date format', 'time format'],
  },
  {
    name: 'Email Settings',
    href: `${APP_ROUTES.SETTINGS}?section=email-settings`,
    icon: Settings,
    section: 'Settings',
    description: 'Control inbox, sending defaults, and email configuration.',
    keywords: ['email', 'sender', 'inbox', 'mail settings'],
  },
  {
    name: 'Automation Intelligence',
    href: `${APP_ROUTES.SETTINGS}?section=automation-intelligence`,
    icon: Settings,
    section: 'Settings',
    description: 'Configure how AI adapts workflows and drafts/sends replies automatically.',
    keywords: ['ai automation', 'intelligence', 'auto send', 'workflow ai'],
  },
  {
    name: 'Payment Methods',
    href: `${APP_ROUTES.SETTINGS}?section=payment-methods`,
    icon: Settings,
    section: 'Settings',
    description: 'Manage available payment options for clients.',
    keywords: ['stripe', 'etransfer', 'manual payment', 'checkout'],
  },
  {
    name: 'Widget Settings',
    href: `${APP_ROUTES.SETTINGS}?section=widget-settings`,
    icon: Settings,
    section: 'Settings',
    description: 'Adjust widget appearance and embedded chat behavior.',
    keywords: ['widget', 'chat widget', 'embed'],
  },
  {
    name: 'Integrations',
    href: `${APP_ROUTES.SETTINGS}?section=integrations`,
    icon: Settings,
    section: 'Settings',
    description: 'Connect external tools and services.',
    keywords: ['integrations', 'google calendar', 'connections'],
  },
  {
    name: 'Email Templates',
    href: `${APP_ROUTES.TEMPLATES}?tab=emails`,
    icon: Settings,
    section: 'Settings',
    description: 'Create reusable email templates and message presets.',
    keywords: ['templates', 'email copy', 'drafts'],
  },
  {
    name: 'Appointment Reminders',
    href: `${APP_ROUTES.SETTINGS}?section=appointment-reminders`,
    icon: Settings,
    section: 'Settings',
    description: 'Set up reminder timing and messaging for appointments.',
    keywords: ['reminders', 'appointments', 'notifications'],
  },
  {
    name: 'Products & Packages',
    href: `${APP_ROUTES.TEMPLATES}?tab=packages`,
    icon: Settings,
    section: 'Settings',
    description: 'Manage your products, services, and pricing packages.',
    keywords: ['packages', 'pricing', 'services', 'products'],
  },
  {
    name: 'Discounts',
    href: `${APP_ROUTES.TEMPLATES}?tab=discounts`,
    icon: Settings,
    section: 'Settings',
    description: 'Manage reusable fixed and percentage discounts.',
    keywords: ['discounts', 'percentage', 'fixed amount', 'vouchers'],
  },
  {
    name: 'Quote & Invoice Templates',
    href: `${APP_ROUTES.TEMPLATES}?tab=quotes`,
    icon: Settings,
    section: 'Settings',
    description: 'Customize quote and invoice template content and layout.',
    keywords: ['quotes', 'invoice templates', 'document layout'],
  },
  {
    name: 'Contract Templates',
    href: `${APP_ROUTES.TEMPLATES}?tab=contracts`,
    icon: Settings,
    section: 'Settings',
    description: 'Edit reusable contract content and document structure.',
    keywords: ['contracts', 'agreements', 'legal templates'],
  },
  {
    name: 'Questionnaire Templates',
    href: `${APP_ROUTES.TEMPLATES}?tab=questionnaires`,
    icon: Settings,
    section: 'Settings',
    description: 'Build intake and questionnaire templates for clients.',
    keywords: ['questionnaires', 'forms', 'intake templates'],
  },
  {
    name: 'AI Training',
    href: `${APP_ROUTES.SETTINGS}?section=ai-training`,
    icon: Settings,
    section: 'Settings',
    description:
      'Train your AI with business data, tone preferences, templates, and past responses.',
    keywords: ['ai', 'training', 'knowledge', 'templates', 'voice', 'tone', 'faq', 'pricing'],
  },
];

export const getGlobalSearchItems = () => [
  ...mainNav,
  ...clientsNav,
  ...shortcutsNav,
  supportNavItem,
  ...workspaceNav,
  ...settingsSearchNav,
];
